import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { Card, CardContent, Grid, Paper, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Title from '../../components/common/Title';

// GraphQL запрос для получения детальной информации о записи аудита
const GET_AUDIT_LOG = gql`
  query GetAuditLog($id: Int!) {
    audit_logs_by_pk(id: $id) {
      id
      user_id
      action
      entity_type
      entity_id
      old_value
      new_value
      created_at
    }
  }
`;

interface AuditLog {
  id: number;
  user_id: number;
  username?: string;
  action: string;
  entity_type: string;
  entity_id: number;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

const AuditLogShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Запрос к API для получения данных
  const { data, loading, error } = useQuery(GET_AUDIT_LOG, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const handleBack = () => {
    navigate('/audit_logs');
  };

  // Функция для форматирования даты
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  // Функция для форматирования JSON
  const formatJson = (value: string | null) => {
    if (!value) return '-';
    try {
      // Пробуем разобрать строку как JSON
      const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
      return JSON.stringify(parsedValue, null, 2);
    } catch (err) {
      // Если не удалось распарсить как JSON, возвращаем как есть
      return value;
    }
  };

  // Стили для полей
  const fieldStyle = {
    backgroundColor: '#f5f5f5',
    padding: '15px',
    borderRadius: '5px',
    marginBottom: '20px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap' as 'pre-wrap',
    wordBreak: 'break-word' as 'break-word'
  };

  const labelStyle = {
    marginBottom: '5px'
  };

  // Кнопка "Назад" для заголовка
  const titleActions = (
    <Button
      variant="contained"
      startIcon={<ArrowBackIcon />}
      onClick={handleBack}
    >
      Назад
    </Button>
  );

  // Если данные загружаются
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Title title="Загрузка деталей аудита..." actions={titleActions} />
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Если произошла ошибка
  if (error) {
    return (
      <Card>
        <CardContent>
          <Title title="Ошибка загрузки данных" actions={titleActions} />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error.message}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Если запись не найдена
  if (!data?.audit_logs_by_pk) {
    return (
      <Card>
        <CardContent>
          <Title title="Запись не найдена" actions={titleActions} />
          <Alert severity="warning" sx={{ mt: 2 }}>
            Запись аудита с ID {id} не найдена
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const auditLog: AuditLog = data.audit_logs_by_pk;

  return (
    <Card>
      <CardContent>
        <Title 
          title={`Детали записи аудита #${auditLog.id}`} 
          actions={titleActions} 
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Основная информация
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography style={labelStyle}>ID</Typography>
                  <Typography>{auditLog.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography style={labelStyle}>Дата создания</Typography>
                  <Typography>{formatDate(auditLog.created_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography style={labelStyle}>Пользователь</Typography>
                  <Typography>{auditLog.username || `ID: ${auditLog.user_id}`}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography style={labelStyle}>Действие</Typography>
                  <Typography>{auditLog.action}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography style={labelStyle}>Тип сущности</Typography>
                  <Typography>{auditLog.entity_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography style={labelStyle}>ID сущности</Typography>
                  <Typography>{auditLog.entity_id}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Изменения
              </Typography>
              <Typography style={labelStyle}>Старое значение</Typography>
              <Box sx={fieldStyle}>
                {formatJson(auditLog.old_value)}
              </Box>
              
              <Typography style={labelStyle}>Новое значение</Typography>
              <Box sx={fieldStyle}>
                {formatJson(auditLog.new_value)}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AuditLogShow; 